import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CustomThemeProvider } from './contexts/ThemeContext';
import { SettingsProvider } from './contexts/SettingsContext';
import { CssBaseline, Box } from '@mui/material';
import Navbar from './components/Navbar';
import Signup from './components/Signup';
import Login from './components/Login';
import Protected from './components/Protected';
import Profile from './components/profile/Profile';
import ProfileSettings from './components/profile/ProfileSettings';
import Dashboard from './components/Dashboard';
import AdminDashboard from './components/admin/AdminDashboard';
import AdminRoute from './components/routes/AdminRoute';
import TeamCreate from './components/team/TeamCreate';
import Teams from './components/team/Teams';
import TeamDetail from './components/team/TeamDetail';
import TeamEdit from './components/team/TeamEdit';
import TournamentCreate from './components/tournament/TournamentCreate';
import TournamentList from './components/tournament/TournamentList';
import TournamentDetail from './components/tournament/TournamentDetail';
import TournamentEdit from './components/tournament/TournamentEdit';
import EventDetail from './components/event/EventDetail';
import EventList from './components/event/EventList';
import EventCreate from './components/event/EventCreate';
import EventEdit from './components/event/EventEdit';
import LiveDisplay from './pages/LiveDisplay';
import Gulag from './pages/Gulag';
import SessionLobby from './pages/SessionLobby';
import Bar from './pages/Bar';
import AdminBar from './pages/AdminBar';
import LandingPage from './components/LandingPage';
import './App.css';
import './styles.css';

// Home Page Component - redirects based on auth status
const HomePage = () => {
    const { isAuthenticated } = useAuth();

    // Show landing page for non-authenticated users
    if (!isAuthenticated) {
        return <LandingPage />;
    }

    // Redirect authenticated users to dashboard
    return <Navigate to="/dashboard" replace />;
};

// Layout wrapper component that conditionally shows navbar
const AppLayout = ({ children, isLoggedIn, setIsLoggedIn }) => {
    const location = useLocation();
    const hideNavbar = location.pathname === '/live' || location.pathname === '/';

    if (hideNavbar) {
        return <>{children}</>;
    }

    // For all other routes, render with navbar
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <Navbar />
            <Box component="main" sx={{ flex: 1 }}>
                {children}
            </Box>
        </Box>
    );
};

function App() {
    const [isLoggedIn, setIsLoggedIn] = useState(false);

    useEffect(() => {
        const token = localStorage.getItem('token');
        setIsLoggedIn(!!token);
    }, []);

    return (
        <SettingsProvider>
            <CustomThemeProvider>
                <CssBaseline />
                <AuthProvider>
                    <Router>
                        <AppLayout isLoggedIn={isLoggedIn} setIsLoggedIn={setIsLoggedIn}>
                        <Routes>
                            <Route path="/" element={<HomePage />} />
                            <Route path="/signup" element={<Signup setIsLoggedIn={setIsLoggedIn} />} />
                            <Route path="/login" element={<Login setIsLoggedIn={setIsLoggedIn} />} />
                            <Route path="/dashboard" element={<Dashboard />} />
                            <Route path="/admin" element={
                                <AdminRoute>
                                    <AdminDashboard />
                                </AdminRoute>
                            } />
                            <Route path="/protected" element={<Protected />} />
                            <Route path="/profile/:userId?" element={<Profile />} />
                            <Route path="/profile/edit" element={<ProfileSettings />} />
                            <Route path="/settings" element={<ProfileSettings />} />
                            <Route path="/team-create" element={<TeamCreate />} />
                            <Route path="/teams" element={<Teams />} />
                            <Route path="/teams/:id" element={<TeamDetail />} />
                            <Route path="/teams/:id/edit" element={<TeamEdit />} />
                            <Route path="/tournament-create" element={<TournamentCreate />} />
                            <Route path="/tournaments" element={<TournamentList />} />
                            <Route path="/tournaments/:id" element={<TournamentDetail />} />
                            <Route path="/tournaments/:id/edit" element={<TournamentEdit />} />
                            <Route path="/events" element={<EventList />} />
                            <Route path="/events/:eventId" element={<EventDetail isLoggedIn={isLoggedIn} />} />
                            <Route path="/events/:eventId/edit" element={
                                <AdminRoute>
                                    <EventEdit />
                                </AdminRoute>
                            } />
                            <Route path="/create-event" element={
                                <AdminRoute>
                                    <EventCreate isLoggedIn={isLoggedIn} />
                                </AdminRoute>
                            } />
                            <Route path="/gulag" element={<Gulag />} />
                            <Route path="/gulag/event/:eventId" element={<Gulag />} />
                            <Route path="/gulag/sessions/:sessionId/lobby" element={<SessionLobby />} />
                            <Route path="/live" element={<LiveDisplay />} />
                            <Route path="/bar" element={<Bar />} />
                            <Route path="/admin/bar" element={
                                <AdminRoute>
                                    <AdminBar />
                                </AdminRoute>
                            } />
                        </Routes>
                        </AppLayout>
                    </Router>
                </AuthProvider>
            </CustomThemeProvider>
        </SettingsProvider>
    );
}

export default App;
